import { apiClient } from './apiClient';
import type {
  BrandPalette,
  Forecast,
  ForecastInput,
  Proposal,
  ProposalIntake,
  ReportInput,
} from '../types/proposal';

// Wire DTOs (backend snake_case). Kept internal; mapped to the camelCase domain
// types below. Frontend talks ONLY through apiClient — never Supabase directly.
interface ProposalWire {
  id: string;
  prospect_key: string;
  title: string;
  website?: string | null;
  service_industry?: string | null;
  location?: string | null;
  status: string;
  created_at: string;
}

interface TierWire {
  name: string;
  budget_usd: number;
  clicks: number;
  conversions: number;
  cpa_usd: number | null;
  jobs: number;
  revenue_usd: number;
  advisories: string[];
}

interface ForecastWire {
  cpc_p75_usd: number | null;
  cpc_band: { low: number | null; median: number | null; high: number | null; p75: number | null };
  tiers: TierWire[];
}

function mapProposal(w: ProposalWire): Proposal {
  return {
    id: w.id,
    prospectKey: w.prospect_key,
    title: w.title,
    website: w.website ?? undefined,
    serviceIndustry: w.service_industry ?? undefined,
    location: w.location ?? undefined,
    status: w.status,
    createdAt: w.created_at,
  };
}

function mapForecast(w: ForecastWire): Forecast {
  return {
    cpcP75Usd: w.cpc_p75_usd,
    cpcBand: w.cpc_band,
    tiers: w.tiers.map((t) => ({
      name: t.name,
      budgetUsd: t.budget_usd,
      clicks: t.clicks,
      conversions: t.conversions,
      cpaUsd: t.cpa_usd,
      jobs: t.jobs,
      revenueUsd: t.revenue_usd,
      advisories: t.advisories,
    })),
  };
}

function reportBody(input: ReportInput) {
  return {
    title: input.title,
    subtitle: input.subtitle,
    intake_budget_usd: input.intakeBudgetUsd,
    cvr: input.cvr,
    close_rate: input.closeRate,
    job_value: input.jobValue,
    cpcs: input.cpcs,
    cpc_mode: input.cpcMode,
    planning_cpcs: input.planningCpcs,
    cpc_base: input.cpcBase,
    cpc_drop_rate: input.cpcDropRate,
    meta_mode: input.metaMode,
    brand: input.brand,
    mode: input.mode,
    paper: input.paper,
    font: input.font,
    website: input.website,
    logo_label: input.logoLabel,
    logo_url: input.logoUrl,
    tiles: input.tiles,
    recommendation_pill: input.recommendationPill,
    read_text: input.readText,
    recommendation_text: input.recommendationText,
    investigation: input.investigation,
  };
}

export const proposalService = {
  // tenantId = the operating agency workspace; the backend scopes rows to it.
  async createProposal(intake: ProposalIntake, tenantId: string): Promise<Proposal> {
    const { data } = await apiClient.post<ProposalWire>(
      '/proposals/',
      {
        prospect_key: intake.prospectKey,
        title: intake.title,
        website: intake.website,
        service_industry: intake.serviceIndustry,
        location: intake.location,
        intake_json: intake.budgetUsd != null ? { budget_usd: intake.budgetUsd } : undefined,
      },
      { params: { tenant_id: tenantId } },
    );
    return mapProposal(data);
  },

  async getProposal(id: string, tenantId: string): Promise<Proposal> {
    const { data } = await apiClient.get<ProposalWire>(`/proposals/${id}`, {
      params: { tenant_id: tenantId },
    });
    return mapProposal(data);
  },

  // History: all saved proposals for the workspace, newest first (no prospect_key).
  async listProposals(tenantId: string): Promise<Proposal[]> {
    const { data } = await apiClient.get<ProposalWire[]>('/proposals/', {
      params: { tenant_id: tenantId },
    });
    return data.map(mapProposal);
  },

  // The stored rendered HTML for a saved proposal (e.g. one Emjae generated in chat).
  async getProposalReport(id: string, tenantId: string): Promise<string> {
    const { data } = await apiClient.get<{ html: string }>(`/proposals/${id}/report`, {
      params: { tenant_id: tenantId },
    });
    return data.html;
  },

  // Full branded report (Stage 7): backend assembles forecast + CPC/Meta toggles
  // + render → HTML the UI previews and prints to PDF.
  async generateReport(input: ReportInput): Promise<{ html: string; cpcMode: string }> {
    const { data } = await apiClient.post<{ html: string; cpc_mode: string }>(
      '/proposals/report',
      reportBody(input),
    );
    return { html: data.html, cpcMode: data.cpc_mode };
  },

  // Same report rendered server-side to a real PDF file (headless Chromium).
  async generateReportPdf(input: ReportInput): Promise<Blob> {
    const { data } = await apiClient.post('/proposals/report/pdf', reportBody(input), {
      responseType: 'blob',
    });
    return data as Blob;
  },

  // Pre-warm the grounding cache when the operator enters the website, so the first
  // ✨ is already fast. No LLM draft — just runs + caches the scrape/keywords/account.
  async warmGrounding(
    website: string,
    tenantId?: string,
    context: Record<string, unknown> = {},
  ): Promise<{ groundedOn: string[]; keywords: number; campaigns: number }> {
    const { data } = await apiClient.post<{ grounded_on: string[]; keywords: number; campaigns: number }>(
      '/proposals/warm-grounding',
      { website, tenant_id: tenantId, context },
      { timeout: 90_000 },
    );
    return { groundedOn: data.grounded_on, keywords: data.keywords, campaigns: data.campaigns };
  },

  // Render the CURRENT (possibly edited-in-preview) HTML straight to PDF.
  async renderPdfFromHtml(html: string, title?: string): Promise<Blob> {
    const { data } = await apiClient.post('/proposals/pdf-from-html', { html, title }, { responseType: 'blob' });
    return data as Blob;
  },

  // Stage-0 intake helper: render the prospect's site and derive a brand theme
  // the operator can accept or tweak before generating the report.
  async extractBrandPalette(url: string): Promise<BrandPalette> {
    const { data } = await apiClient.post<BrandPalette>('/proposals/brand-palette', { url });
    return data;
  },

  // Re-derive the theme when the operator manually assigns primary/accent from
  // the swatches (same backend derivation as the auto-pick — no drift).
  async deriveTheme(primary: string, accent: string): Promise<BrandPalette> {
    const { data } = await apiClient.post<BrandPalette>('/proposals/brand-theme', { primary, accent });
    return data;
  },

  // AI-draft report fields, grounded on intake + live site scrape + Ahrefs keyword
  // research + (when the prospect is already a client) a live campaign read.
  async suggestFields(
    fields: string[],
    context: Record<string, unknown>,
    website?: string,
    tenantId?: string,
    count = 1,
  ): Promise<{ options: Record<string, unknown>; groundedOn: string[] }> {
    // options: { field: [alternatives] } (tiles → [{k,v}]).
    const { data } = await apiClient.post<{ options: Record<string, unknown>; grounded_on: string[] }>(
      '/proposals/suggest',
      { fields, context, website, tenant_id: tenantId, count },
      { timeout: 90_000 }, // grounding (scrape + keywords + live account read + LLM) can take >30s cold
    );
    return { options: data.options, groundedOn: data.grounded_on };
  },

  async runForecast(input: ForecastInput): Promise<Forecast> {
    const { data } = await apiClient.post<ForecastWire>('/proposals/forecast', {
      intake_budget_usd: input.intakeBudgetUsd,
      cpcs: input.cpcs,
      cvr: input.cvr,
      close_rate: input.closeRate,
      job_value: input.jobValue,
      demand_ceiling_clicks_per_day: input.demandCeilingClicksPerDay,
    });
    return mapForecast(data);
  },
};
